// Eén dependency-vrije bron voor het contactpad op de zakelijke pagina en
// alle afgeleide pagina's onder /zakelijk/*. Favotrip is voor zakelijke
// bezoekers NIET telefonisch of via WhatsApp bereikbaar als ALGEMENE
// belofte: Sophie (ZakelijkAssistant, alleen waar zij gemount is) en
// e-mail zijn de kanalen voor de gewone pagina-copy. Routes zonder Sophie
// (bevestigd, bedankt, vouchers-op-factuur) verwijzen uitsluitend naar het
// e-mailadres; de bron-scan zakelijk-contact-channel-subtree.regression.
// test.mjs bewaakt dat er in de hele subboom geen tel:/wa.me/WhatsApp-
// belofte terugkomt.
//
// Uitzondering (Dennis, 17-09-2026, twee losse berichten): "ze kunnen
// altijd de klantenservice bellen" -- MAAR "Dat telefoonnummer van de
// klantenservice moet wel echt alleen op het juiste moment in beeld komen.
// Niet iedereen die zomaar gaat bellen naar het telefoonnummer." Het
// nummer hieronder (`escalationPhone`/`escalationPhoneHref`, dezelfde
// tel:-link als de live /contact-pagina en WhatsAppButton.js's
// `WHATSAPP_NUMBER`-fallback) mag UITSLUITEND gebruikt worden op de twee
// momenten waarop iemand echt vastloopt of op het punt staat te kopen:
//   1. De foutmelding bij een vastgelopen bestelling in
//      BusinessQuoteForm.js (de `confirmationMailFailed`-tak: de
//      bevestigingsmail is aantoonbaar niet verstuurd).
//   2. Sophie's handoff-tekst in ZakelijkAssistant.js, als zij een vraag
//      niet kan beantwoorden.
// NERGENS anders: niet in de hero, niet in "De zekerheden", niet bij "Je
// aanspreekpunt", niet in de FAQ. De uitsluiting is over het MOMENT, niet
// over de URL -- dus dit geldt ook voor de PAGINACOPY van
// /zakelijk/overzicht (hero, zekerheden, aanspreekpunt, FAQ, de
// overzichtstekst zelf): dat is precies de bellers-met-algemene-vragen-
// groep die Dennis niet wil.
//
// PR #1419 ronde 2 (verduidelijking, geen nieuwe regel): dit sluit NIET
// Sophie's echte escalatie uit, ook niet als zij toevallig op
// /zakelijk/overzicht gemount is -- een bezoeker bij wie Sophie een ECHTE
// escalatie heeft (de backend geeft zelf `needsHuman: true` terug, zie
// `handoffEscalated` in ZakelijkAssistant.js) zit op HETZELFDE
// escalatiemoment als op /zakelijk zelf, ongeacht de route waarop dat
// gebeurt. Wat uitgesloten blijft is dus de vaste paginacopy, niet Sophie's
// dynamische, aan een echte escalatie gebonden tekst.
//
// PR #1419 ronde 5 (correctie, BLOK-B): `blockedByGuard` en
// `quickReplyBlocked` -- Sophies EIGEN hygiëne wanneer haar antwoord of een
// quick reply de denylist raakt -- zijn GEEN escalatiemoment en horen dus
// NIET in de zin hierboven, ondanks wat een eerdere versie van deze
// docblock zei. Ze openen wel het handoff-formulier (`handoffOpen`), maar
// zonder dat `handoffEscalated` aan gaat: het antwoord kan compleet en
// correct zijn geweest, of de bezoeker heeft niets geklikt. Alleen een
// echte `needsHuman: true` van de backend -- vóór die guard-forcering --
// zet het nummer aan.
//
// Derde ingang (PR #1419 ronde 5, nieuw gedocumenteerd, geen gedragswijziging):
// zowel een BFF-fout/uitval als een 25s-timeout in
// `src/app/api/zakelijk/assistent/chat/route.js` (de `catch`-tak) als een
// fetch-throw in `ZakelijkAssistant.js`'s `ask()` geven zelf een synthetisch
// `needsHuman: true` terug met status 200. Dat is inhoudelijk verdedigbaar
// ("Sophie kwam er niet uit" klopt dan letterlijk), maar het is de BREEDSTE
// ingang die er is: hij hangt aan de beschikbaarheid/snelheid van de
// LLM-call, niet aan wat de bezoeker vroeg. Een kapotte of trage Sophie
// toont dus het nummer aan iedereen die op dat moment toevallig een vraag
// stelt. Bewust geaccepteerd gedrag, geen verrassing.
//
// De poort (zakelijk-contact-channel-subtree.regression.test.mjs, test
// "escalatie-telefoonverwijzing...") dwingt deze allowlist van twee
// bestanden af door te scannen op elke `escalationPhone`/
// `escalationPhoneHref`-referentie in de subboom -- niet alleen
// documenteren.
export const ZAKELIJK_CONTACT = {
  assistantCta: "Stel ze aan Sophie rechtsonder",
  email: "klantenservice@favotrip.nl",
  emailHref: "mailto:klantenservice@favotrip.nl",
  escalationPhone: "+31 20 261 4916",
  escalationPhoneHref: "tel:+31202614916",
};

// Codex-review PR #1308 ronde 9 (blokkerend): de bron-scan hierboven dekte
// alleen ONZE eigen productiecode onder /zakelijk -- niet de tekst die de
// backend (een ANDER systeem, andere release-cadans) via
// `/api/zakelijk/direct-bestellen`'s `message`-veld teruggeeft
// (`src/app/api/zakelijk/direct-bestellen/route.js`). Die backend-tekst
// werd tot deze ronde ongewijzigd doorgestuurd en 1-op-1 gerenderd in
// `BusinessQuoteForm.js` (zowel de mail-faalde- als de normale
// QUOTE-branch) -- een lek van precies de verboden telefoon/WhatsApp-
// belofte, buiten bereik van de source-scan omdat die geen backend-repo
// leest. Deze denylist-sanitizer (geen allowlist -- zie de docstring bij
// `containsForbiddenContactChannel` hieronder) is de ENE gedeelde bron voor "bevat dit
// tekst-fragment een verboden contactkanaal-belofte": zowel
// `direct-bestellen/route.js` (voordat het `message`-veld het antwoord
// verlaat) als de bron-scan (`zakelijk-contact-channel-subtree.regression.
// test.mjs`, die dit bestand nu importeert i.p.v. zijn eigen kopie van de
// verbodslijst te onderhouden) gebruiken dezelfde patronen -- geen twee
// plekken die uit elkaar kunnen lopen.
// Bouwreparatie 23-09-2026 (Dennis, "Sophie werkt op mobiel heel slecht"):
// deze substringpatronen matchten ook BINNEN andere woorden -- "hotel:"
// (Sophie's eigen "Efteling, tickets en hotel: 1 nacht"-antwoord bevat de
// kale substring "tel:"), "label of" ("bel of"), "recall us" ("call us").
// Woordgrenzen (`\b`) om die te sluiten zonder de echte vormen te missen:
// een grens bestaat alleen tussen een letter/cijfer en een niet-letter/
// cijfer (of string-begin/-eind), dus "hotel:" (o voor t) en "label of"
// (a voor b) blijven vrij, terwijl "tel:+31...", `href="tel:..."`,
// "Tel: 020 261 4916" en "Bel of app ons" gewoon blijven blokkeren.
export const FORBIDDEN_CONTACT_CHANNEL_PATTERNS = [
  /\btel:/i,
  /wa\.me/i,
  /whatsapp/i,
  /\+31/,
  /\bbel ons\b/i,
  // Codex-review ronde 8 (blokkerend, op de source-scan): "Bel of app ons
  // gerust" sloop door de eerdere lijst heen -- "app ons" was hetzelfde
  // verboden kanaal (WhatsApp) in een vorm die de lijst niet dekte. De
  // overige varianten (NL/DE/FR/PL) staan er alvast tegen toekomstige
  // vertalingen/rondes bij, niet omdat ze vandaag ergens voorkomen.
  //
  // F4 (opus-review PR #1435, LOW): "belt u"/"belt je"/"bellen we je" stond
  // hier eerder bewust NIET in, met als reden `BusinessHoursScheduler.js`'s
  // legitieme advies-gesprek-terugbelfeature ("Dennis belt je op of rond
  // dit moment"). Dat adviesgesprek is vervallen (Casper+Dennis 21-09,
  // dezelfde ronde die deze PR uitvoert) en het bestand is verwijderd --
  // de uitzonderingsreden bestaat niet meer, dus het patroon gaat gewoon
  // in de verbodslijst.
  /\bbel(t|len we) (u|je)\b/i,
  /\bapp ons\b/i,
  // Word-bounded: een kale "appen" substring-matcht ook "stappen"/
  // "boodschappen"/React Hook Form's `append` -- vals positieven die niet
  // de verboden WhatsApp-belofte zijn. Scope tot het losstaande werkwoord.
  /\bappen\b/i,
  /\bbel of\b/i,
  /\beven bellen\b/i,
  /telefonisch/i,
  /\brufen sie\b/i,
  /\bruf uns an\b/i,
  /anrufen/i,
  /appelez-nous/i,
  /appelez nous/i,
  /\bcall us\b/i,
  /zadzwoń/i,
];

// Opus-review PR #1308 ronde 12 (blokkerend, B1): eerste poging voegde
// hier `/telefoonnummer/i`, `/telefoonnr/i`, `/tel\.?\s?nr/i` toe -- dat
// verving de bron-scan-vondst (finding 2 CI-run): `copy.js`'s eigen
// "Telefoonnummer" formulierlabel/e-mailtemplate en
// `BusinessQuoteForm.js`'s `<label>Telefoonnummer *</label>` zijn een
// legitieme vraag OM het telefoonnummer VAN de bezoeker (contactgegevens
// bij een bestelling), geen belofte dat Favotrip zelf telefonisch
// bereikbaar is -- exact het soort valse-positief dat een woordpatroon
// zonder cijfercontext oplevert. De B1-doelcase ("Telefoonnummer:
// 085.123.4567") wordt al gedekt doordat `DIGIT_CLUSTER_RE` hieronder nu
// óók punten als scheidingsteken telt (het bijbehorende cijferpatroon
// matcht de NL-nummerklasse) -- geen los woordpatroon nodig, en dus ook
// geen risico op deze valse-positief.

// Bare-number detectie: een telefoonnummer zonder `tel:`-schema slipt langs
// `FORBIDDEN_CONTACT_CHANNEL_PATTERNS` heen -- bv. "06 12345678",
// "088-1234567" of "0031 6 12345678".
//
// Opus-review PR #1308 ronde 11 (blokkerend, ronde 10 dekte alleen het
// EIGEN supportnummer `0202614916` hardgecodeerd): elk ANDER nummer --
// mobiel, een ander landnetnummer, buitenlands -- sloop er ongehinderd
// doorheen. Opus-review ronde 10+11 (B4, blokkerend op DIE fix): de eerste
// generieke poging eiste exact ÉÉN scheiding gevolgd door 6-7 aaneen-
// gesloten cijfers -- maar Nederlandse nummers worden ook 3-3-4 of
// 3-3-2-2 gegroepeerd ("020 261 4916", "010 123 45 67"), dus die vorm liet
// zowel het eigen nummer als gangbare groeperingen weer LEKKEN. Dit is een
// DENYLIST, geen allowlist: we blokkeren bekende telefoonnummer-VORMEN, we
// keuren geen specifieke inhoud goed.
//
// Aanpak (drie stappen, ongevoelig voor de groepering):
// 0. `maskIbanCandidates` (Opus-review ronde 12, B2, blokkerend) maskeert
//    IBAN-vormige tokens (`NL91 ABNA 0417 1643 00`, gespatieerd of
//    aaneengesloten) VOORDAT cijferclusters gezocht worden. Zonder dit
//    masker vormt de gespatieerde variant een geldige "grens" (de spatie
//    tussen "ABNA" en "0417" telt niet als een letter/cijfer-grens), dus
//    "0417 1643 00" werd los gelezen als een 10-cijferig cijfercluster
//    ("0417164300") en zou als NL-nummer geblokkeerd worden -- de hele
//    factuurregel (met een IBAN erin) zou dan onterecht vervangen worden.
//    De aaneengesloten vorm was al veilig (de letter-grens vlak voor het
//    cijferblok blokkeerde die al via de lookbehind in stap 1).
// 1. `extractDigitClusterCandidates` vindt "cijferclusters" in de
//    GEMASKEERDE tekst: een cijfer, gevolgd door 6-19 tekens cijfers/
//    spaties/streepjes/haakjes/punten/unicode-streepjes, gevolgd door een
//    cijfer, optioneel met een leidende "+", en NOOIT direct grenzend aan
//    een letter of ander cijfer (dus nooit een los stuk uit een langere
//    alfanumerieke string).
//
//    Opus-review ronde 12 (B1, blokkerend): punten (".") en unicode-
//    streepjes (U+2010 t/m U+2014 "‐‑‒–—", U+2212 "−") zijn nu WEL
//    scheidingstekens -- "Telefoonnummer: 085.123.4567" en een
//    FR/BE-genoteerd "06.12.34.56.78" sloopten er anders ongehinderd
//    doorheen. De oude rechtvaardiging (punten uitsluiten voorkomt dat
//    "09.30-17.30 uur" tot een vals-positieve 0900-match samensmelt) klopt
//    niet meer als reden om punten helemaal te weren: stap 2's
//    klasse-toets (exact 10 cijfers voor een lokaal nummer, of het
//    0800/0900-patroon) ving dat destijds al af zonder de punt-uitsluiting
//    nodig te hebben. Wat WEL een nieuwe vals-positief bleek: "08.00-12.00"
//    (8 cijfers, "0800" + 4 cijfers) matcht toevallig het 0800/0900-
//    premium-patroon. Stap 2b (`looksLikeTimeRange`) sluit dat expliciet
//    uit voordat de nummerklassen getoetst worden.
// 2. `isForbiddenPhoneCluster` haalt de scheidingstekens uit zo'n cluster
//    en toetst het resultaat tegen bekende NL-nummerklassen (10-cijferig
//    `0[1-9]xxxxxxxx` -- dekt 06-mobiel en alle netnummers incl. 010/020/
//    085/088 in één patroon, ongeacht de groepering; 0800/0900 met 4-7
//    cijfers) en tegen internationale landcodes (NL/BE/DE/FR/UK/PL, met of
//    zonder "+", of via het "00"-prefix) waarbij het nationale nummer na de
//    landcode 8-12 cijfers telt. Cijferreeksen die niet met "0" beginnen en
//    geen "+"/landcode hebben (KvK "96276762", een ordernummer
//    "20260904-1234", een datum "05-09-2026" -- te kort voor elke klasse
//    hierboven) worden nooit als telefoonnummer geclassificeerd.
// 2b. `looksLikeTimeRange` (Opus-review ronde 12, blokkerend op stap 1's
//    punt-toevoeging): een cluster die zelf de vorm "uu.mm-uu.mm" of
//    "uu:mm-uu:mm" heeft (met of zonder spaties, elk unicode-streepje als
//    scheider) wordt NOOIT als telefoonnummer geclassificeerd, ongeacht
//    wat de cijferklassen hierboven zeggen -- dit is precies de vorm van
//    "08.00-12.00"/"13.00-17.00" (en dus ook "Tijden 08.00-12.00 en
//    13.00-17.00 uur", dat "en" breekt de twee losse clusters toch al uit
//    elkaar via de woordgrens in stap 1).
//
// De expliciete eigen-supportnummerpatronen uit ronde 10 zijn niet apart
// gehouden: de generieke 10-cijferige NL-klasse hierboven is een strikte
// superset (elk eigen nummer is óók gewoon een geldig NL-nummer), dus een
// aparte kopie zou nooit iets extra vangen terwijl hij wel apart
// onderhouden moest worden -- exact de denylist-unie-fout die B4 meldde
// over ronde 11's verwijdering. De cluster-aanpak IS de unie.
const IBAN_RE =
  /\b[A-Za-z]{2}\d{2}(?:[ ]?[A-Za-z0-9]{4}){2,7}(?:[ ]?[A-Za-z0-9]{1,4})?\b/g;

function maskIbanCandidates(text) {
  return text.replace(IBAN_RE, (match) => "#".repeat(match.length));
}

// Opus-review PR #1308 ronde 17 (blokkerend, B1 — regressie t.o.v.
// df3d9b3d/ronde 16): de venster-scan hierboven doorbreekt de bestaande
// tijdsbestek-uitzondering zodra twee tijdsbestekken elkaar RAKEN zonder
// een woordgrens ertussen (geen "en", gewoon een spatie of niets) --
// "Tijden 08.00-12.00 13.00-17.00 uur" heeft geen letter tussen de twee
// paren, dus de hele reeks "08.00-12.00 13.00-17.00" is voor
// `DIGIT_CLUSTER_RE` ÉÉN doorlopend cijfercluster; de venstergenerator
// (die alle aaneengesloten deelreeksen van cijfergroepen aftast) vindt
// daar toevallig vensters in die een geldige nummerklasse matchen
// ("0800120013" -- 10 cijfers, NL_LOCAL_10_DIGIT; "09001000" -- 0900 +
// 4 cijfers, NL_TOLLFREE_PREMIUM). `looksLikeTimeRange` hieronder redt dit
// niet: die toetst het hele cluster (of, sinds ronde 16, een heel venster),
// niet de oorspronkelijke tijdnotatie-tokens waaruit het cluster is
// opgebouwd.
//
// Fix: maskeer elk op-zichzelf-staand hh.mm/hh:mm-tijdtoken VOORDAT de
// cijferclusters gezocht worden (zelfde patroon als `maskIbanCandidates`
// hierboven) -- dan levert het nooit meer cijfergroepen voor de
// venster-scan. Een "chain" is een MAXIMALE reeks van met "." of ":"
// gescheiden cijfergroepen (spatie/streepje zitten hier bewust niet in de
// scheider-klasse: die breken tijdsbestekken sowieso al uit elkaar in
// losse chains). Een chain wordt alleen als tijd gemaskeerd als hij EXACT
// twee groepen heeft die samen een geldige tijd vormen (uur 0-23, minuut
// exact 2 cijfers 0-59):
// - "08.00-12.00" heeft geen "." of ":" over het streepje heen, dus dit
//   valt uiteen in twee losse tweegroeps-chains ("08.00" en "12.00"),
//   allebei gemaskeerd -- exact de bug-case, ook zonder "en" ertussen.
// - "06.12.34.56.78" (FR/BE-notatie, moet GEBLOKKEERD blijven) is ÉÉN
//   chain van VIJF groepen: de chain-regex is greedy en consumeert de
//   hele met-punten-gescheiden reeks in één keer, dus "06.12" wordt nooit
//   losgeknipt als tweegroeps-tijdtoken zolang er nog een ".dd"-groep op
//   volgt. Groepsaantal != 2 -> niet gemaskeerd, blijft intact voor de
//   bestaande cluster/klasse-toets.
// - "085.123.4567" (moet GEBLOKKEERD blijven) is om dezelfde reden een
//   chain van DRIE groepen (085/123/4567) -- niet gemaskeerd.
// Geen lookbehind (Safari/iOS < 16.4-compatibiliteit, zie ronde 15
// hierboven): de grens VOOR de chain wordt als vangende groep gelezen
// i.p.v. als voorwaarde getoetst, net als bij `DIGIT_CLUSTER_RE`.
const TIME_CHAIN_RE =
  /(^|[^A-Za-z0-9.:])(\d{1,3}(?:[.:]\d{1,3}){0,10})(?![A-Za-z0-9.:])/g;

function isValidTimeChain(chain) {
  const groups = chain.split(/[.:]/);
  if (groups.length !== 2) return false;
  const [hh, mm] = groups;
  if (mm.length !== 2) return false;
  const hourNum = Number(hh);
  const minuteNum = Number(mm);
  if (!Number.isInteger(hourNum) || hourNum < 0 || hourNum > 23) return false;
  if (!Number.isInteger(minuteNum) || minuteNum < 0 || minuteNum > 59) {
    return false;
  }
  return true;
}

function maskTimeTokens(text) {
  return text.replace(TIME_CHAIN_RE, (full, boundary, chain) => {
    if (!isValidTimeChain(chain)) return full;
    return boundary + "#".repeat(chain.length);
  });
}

// Opus-review PR #1308 ronde 13 (blokkerend, regressie t.o.v. 64a3550c0):
// een kale "." in de scheider-klasse hierboven was niet te onderscheiden
// van een zin-afsluitende punt. Volgde er ná die punt nog iets met
// cijfers (een vervolgzin die met een cijfer begint, of gewoon een tweede
// nummer), dan smolten beide tot ÉÉN cijfercluster van meer dan de 21-
// tekens-limiet samen; de regex-engine backtrackte dan naar een kortere
// match die middenin het eerste nummer eindigt ("020 261 4916" werd
// bijvoorbeeld "020 261 491"), en die afgekapte reeks matcht geen enkele
// klasse in `isForbiddenPhoneCluster` -- het nummer lekte alsnog door.
// Concrete lekken op de vorige versie: "Ons nummer: 020 261 4916. 24 uur
// per dag bereikbaar.", "... 7 dagen per week.", "Nummer 085 123 4567. 2
// minuten wachttijd.", "Onze nummers: 020 261 4916. 085 123 4567.",
// "Contact: 06 12345678. 8.00-17.00 uur." De punt moet dus WEL als
// scheidingsteken binnen één nummer tellen ("085.123.4567",
// "06.12.34.56.78" -- zie ronde 12), maar NIET als hij een zin afsluit.
// Vervanging: i.p.v. een kale "." in de tekenklasse, een aparte alternatie-
// tak `\.(?=\d)` -- de punt telt alleen mee als er DIRECT een cijfer op
// volgt (zonder dat cijfer zelf te verbruiken, zodat de repetitie-telling
// niet dubbelt). Een punt gevolgd door een spatie/hoofdletter/einde string
// (zin-afsluitend) breekt de cluster nu af op precies die punt, zodat het
// daarvóór liggende nummer los en compleet overblijft voor classificatie.
// Codex-review PR #1308 ronde 15 (blokkerend): de negative lookbehind
// `(?<![A-Za-z0-9])` hierboven wordt niet geparsed door Safari/iOS < 16.4
// (lookbehind landde daar pas in maart 2023) -- dit bestand wordt zowel
// server-side (sanitizer in de routes) als client-side geïmporteerd door
// `BusinessQuoteForm.js` en `ZakelijkAssistant.js`, dus een SyntaxError bij
// het parsen van deze regex zou de HELE zakelijke clientchunk laten
// crashen op elke browser ouder dan Safari 16.4/iOS 16.4. Geen
// `.browserslistrc`/`browserslist`-key in package.json gevonden om dit
// expliciet af te dwingen of uit te sluiten; Next 16's SWC-standaard-
// "modern"-doeltaal bevat historisch Safari 12, dus dit was een reëel
// crashrisico, niet alleen theoretisch. Vervangen door een lookbehind-vrije
// vorm: groep 1 vangt het grenskarakter (of stringbegin) VOOR de cluster
// i.p.v. het als voorwaarde te toetsen zonder te verbruiken; groep 2 is de
// cluster zelf. `(?![A-Za-z0-9])` (lookAHEAD, geen lookbehind) en
// `\.(?=\d)` blijven ongewijzigd -- lookahead wordt al sinds ES3 breed
// ondersteund, dat is niet het probleem. Bij twee clusters die maar door
// één grenskarakter gescheiden zijn (bv. "020 261 4916. 085 123 4567")
// faalt de match-poging die dat karakter als grens EN clusterstart
// probeert; de regex-engine schuift dan gewoon één positie op, dus de
// tweede cluster wordt alsnog los gevonden vanaf de spatie ervoor (getest
// in `zakelijk-contact.test.mjs`, inclusief exact dit voorbeeld).
// Opus-review PR #1308 ronde 15 delta (blokkerend, B1): de vaste
// bovengrens `{6,19}` beperkte niet alleen de regex-kost, maar bepaalde ook
// hoeveel van een langere cijfer-plus-scheider-run als ÉÉN kandidaat werd
// getoetst. Zodra een echt nummer zonder zin-afsluitend leesteken werd
// gevolgd door NOG meer cijfers ("020 261 4916 24 uur per dag bereikbaar."
// -- spatie i.p.v. punt na "4916"), smolt de hele run (12 cijfers) tot één
// cluster dat GEEN van de nummerklassen hieronder meer matcht (10-cijferig
// exact, of 0800/0900+4-7, of landcode-totaal) -- het echte nummer zelf was
// nog prima classificeerbaar, maar werd nooit als LOS kandidaat getoetst.
// Negen live-reproducties (zie zakelijk-contact.test.mjs, "ronde 16")
// bevestigden dit voor elke scheider (spatie/streepje/haakje) en voor het
// internationale "0031 (0) 6 12 34 56 78" (22 tekens, NB-1).
//
// Structurele fix, twee delen:
// 1. De bovengrens gaat van `{6,19}` naar `{6,120}` -- ruim genoeg voor
//    meerdere aaneengeplakte nummers/tijdsbestekken in één zin, met een
//    plafond tegen pathologische invoer (regex-kost blijft lineair: de
//    alternatie bevat geen geneste kwantoren).
// 2. `extractDigitClusterCandidates` toetst niet langer alleen de HELE
//    cluster: hij splitst 'm in cijfergroepen (`\d+`-runs, de scheiders
//    ertussen genegeerd) en genereert alle AANEENGESLOTEN vensters van die
//    groepen (venster = een contiguë deelreeks) wiens samengevoegde
//    cijferlengte 8-15 is -- de spanwijdte van elke bekende klasse
//    hieronder. Elk venster wordt teruggegeven als de RUWE substring
//    (inclusief originele scheiders, en een voorloop-"+" als het venster
//    bij het begin van de cluster begint en de cluster er een had), zodat
//    `isForbiddenPhoneCluster` ongewijzigd blijft -- die doet zijn eigen
//    cijfer-strip en `looksLikeTimeRange`-toets op precies die substring.
//    Een treffer in ÉÉN venster is genoeg (`containsBarePhoneNumber` blijft
//    `.some(...)`), dus het externe supportnummer in "Ons nummer: 020 261
//    4916 24 uur per dag bereikbaar." wordt gevonden via het venster dat
//    alleen "020 261 4916" beslaat, ook al matcht de volledige cluster
//    (mét de trailing "24") geen enkele klasse.
//
//    Plausibiliteitsregel (voorkomt nieuwe vals-positieven van de
//    venster-scan zelf): een venster van MEER dan één groep wordt
//    afgekeurd als een van zijn groepen een jaartal-vorm heeft
//    (`/^(19|20)\d{2}$/`) -- "bestelling 2026 0912 voor 10 personen" en
//    "0912 2026 10 personen" zouden anders als 8/10-cijferig NL-nummer
//    geclassificeerd worden (`isForbiddenPhoneCluster` kent geen jaartal-
//    uitzondering, hij ziet alleen cijfers). Een venster met een los
//    jaartal ALS ENIGE groep is toch al te kort (4 cijfers, onder de
//    8-cijfer-ondergrens) om ergens te matchen. Twaalf legitieme
//    meerdere-cijfergroepen-zinnen (datum+aantal, factuurnummer+bedrag,
//    postcode+huisnummer+aantal, "20 collega's 175 euro 5 dagen", enz.)
//    staan als NEGATIVES in zakelijk-contact.test.mjs; geen van hun groepen
//    is jaartal-vormig, dus de regel raakt ze niet. Er is BEWUST geen
//    aparte cap op de LENGTE van een losse groep: die zou "06 12345678"
//    (mobiel, 2-8 gegroepeerd) raken via z'n 8-cijferige tweede-groep-
//    venster. Op het AANTAL groepen per venster staat wel een cap
//    (`MAX_WINDOW_GROUPS` hieronder, ronde 16) -- niet als vals-
//    positiefbescherming, maar puur defensief tegen combinatorische
//    explosie; die staat ruim boven de 7 groepen die het internationale
//    "0031 (0) 6 12 34 56 78" (0031/0/6/12/34/56/78) nodig heeft, dus geen
//    van de bekende nummervormen wordt erdoor geraakt. De 8-15-cijfer-
//    lengtegrens plus de precieze klasse-toetsen in
//    `isForbiddenPhoneCluster` begrenzen de vals-positief-ruimte verder.
const DIGIT_CLUSTER_RE =
  /(^|[^A-Za-z0-9])(\+?\d(?:[\d\s\-()‐-—−]|\.(?=\d)){6,120}\d)(?![A-Za-z0-9])/g;

const NL_LOCAL_10_DIGIT = /^0[1-9]\d{8}$/;
const NL_TOLLFREE_PREMIUM = /^0(800|900)\d{4,7}$/;
const COUNTRY_CODE_PREFIXES = ["31", "32", "49", "33", "44", "48"];

// Opus-review ronde 12 (blokkerend): "uu.mm-uu.mm" / "uu:mm-uu:mm", elk
// unicode-streepje als scheider, spaties rond de scheider toegestaan.
const TIME_RANGE_RE = /^\d{1,2}[.:]\d{2}\s*[-‐-—−]\s*\d{1,2}[.:]\d{2}$/;

function looksLikeTimeRange(cluster) {
  return TIME_RANGE_RE.test(cluster.trim());
}

// Jaartal-vorm (1900-2099) -- zie de plausibiliteitsregel hierboven bij
// DIGIT_CLUSTER_RE. Alleen gebruikt om een MEERGROEPS-venster af te keuren,
// nooit om een los cijfer-cluster an sich te classificeren.
const YEAR_LIKE_RE = /^(19|20)\d{2}$/;

// Bovengrens op het aantal groepen per venster: puur een defensieve cap
// tegen combinatorische explosie bij pathologisch veel losse cijfergroepen
// in één cluster (customer-messages zijn kort); ruim boven de 7 groepen
// die "0031 (0) 6 12 34 56 78" nodig heeft, dus geen van de bekende
// nummervormen wordt hierdoor geraakt.
const MAX_WINDOW_GROUPS = 10;

function isPlausiblePhoneWindow(groups) {
  if (groups.length > MAX_WINDOW_GROUPS) return false;
  if (groups.length > 1 && groups.some((g) => YEAR_LIKE_RE.test(g))) {
    return false;
  }
  return true;
}

function extractDigitClusterCandidates(text) {
  const masked = maskTimeTokens(maskIbanCandidates(text));
  const candidates = [];
  for (const match of masked.matchAll(DIGIT_CLUSTER_RE)) {
    const cluster = match[2];
    const clusterHasPlus = cluster.startsWith("+");
    const groups = [];
    const groupRe = /\d+/g;
    let groupMatch;
    while ((groupMatch = groupRe.exec(cluster))) {
      groups.push({
        digits: groupMatch[0],
        start: groupMatch.index,
        end: groupMatch.index + groupMatch[0].length,
      });
    }
    for (let start = 0; start < groups.length; start += 1) {
      for (
        let end = start;
        end < groups.length && end - start < MAX_WINDOW_GROUPS;
        end += 1
      ) {
        const windowGroups = groups.slice(start, end + 1);
        if (!isPlausiblePhoneWindow(windowGroups.map((g) => g.digits))) {
          continue;
        }
        const digits = windowGroups.map((g) => g.digits).join("");
        if (digits.length < 8 || digits.length > 15) continue;
        const sliceStart =
          clusterHasPlus && start === 0 ? 0 : windowGroups[0].start;
        const rawWindow = cluster.slice(
          sliceStart,
          windowGroups[windowGroups.length - 1].end,
        );
        candidates.push(rawWindow);
      }
    }
  }
  return candidates;
}

function isForbiddenPhoneCluster(cluster) {
  if (looksLikeTimeRange(cluster)) return false;
  const hasPlus = cluster.trimStart().startsWith("+");
  const digits = cluster.replace(/\D/g, "");
  if (!digits) return false;

  if (digits.startsWith("0")) {
    if (NL_LOCAL_10_DIGIT.test(digits)) return true;
    if (NL_TOLLFREE_PREMIUM.test(digits)) return true;
    // "00"-prefix internationaal (bv. "0031 6 12345678"): het nationale
    // nummer na de landcode (met een eventuele meegetypte trunk-"0"
    // genegeerd) moet 8-10 cijfers tellen.
    for (const cc of COUNTRY_CODE_PREFIXES) {
      if (digits.startsWith(`00${cc}`)) {
        const rest = digits.slice(4).replace(/^0/, "");
        if (rest.length >= 8 && rest.length <= 12) return true;
      }
    }
    return false;
  }

  if (hasPlus) {
    for (const cc of COUNTRY_CODE_PREFIXES) {
      if (digits.startsWith(cc)) {
        const rest = digits.slice(cc.length).replace(/^0/, "");
        if (rest.length >= 8 && rest.length <= 12) return true;
      }
    }
  }
  return false;
}

/**
 * True als `text` ergens een kaal telefoonnummer bevat (elke gangbare NL-
 * groepering, of een internationaal nummer met landcode). Zie de docstring
 * hierboven voor het twee-stappen-ontwerp; dit is de export die zowel
 * `containsForbiddenContactChannel` hieronder als de bron-scan
 * (`zakelijk-contact-channel-subtree.regression.test.mjs`) gebruiken.
 */
export function containsBarePhoneNumber(text) {
  if (typeof text !== "string" || text.length === 0) return false;
  return extractDigitClusterCandidates(text).some(isForbiddenPhoneCluster);
}

const DEFAULT_SAFE_CUSTOMER_MESSAGE =
  "Stel je vraag aan Sophie rechtsonder, of mail klantenservice@favotrip.nl.";

// Codex-review PR #1308 ronde 13 (blokkerend, finding 2): de generieke
// fallback hierboven noemt Sophie, maar niet elke `/api/zakelijk/*`-route
// wordt aangeroepen vanaf een pagina waar `ZakelijkAssistant` gemount is --
// `/zakelijk/vouchers-op-factuur` (`B2bQuoteForm.js` ->
// `b2b-offerte/route.js`) heeft geen Sophie-launcher, dus "Sophie
// rechtsonder" zou daar zelf een niet-bestaand kanaal beloven. Canonieke
// e-mail-only tekst voor precies die routes/pagina's -- zelfde patroon als
// `SupportLinks.js`'s "Mail ons op klantenservice@favotrip.nl" op de
// tokengated bevestigd/bedankt-pagina's, die om dezelfde reden geen Sophie
// noemen.
export const DEFAULT_SAFE_EMAIL_ONLY_MESSAGE = `Mail ons op ${ZAKELIJK_CONTACT.email}.`;

/**
 * True als `text` een verboden contactkanaal-belofte (telefoon/WhatsApp,
 * in NL/DE/FR/PL-varianten, of een kaal telefoonnummer) bevat. Dit is een
 * DENYLIST-check (`FORBIDDEN_CONTACT_CHANNEL_PATTERNS` +
 * `containsBarePhoneNumber`): tekst die geen van de bekende verboden vormen
 * raakt, telt als toegestaan -- er is geen positieve lijst van "veilige"
 * tekst waartegen gevalideerd wordt.
 */
export function containsForbiddenContactChannel(text) {
  if (typeof text !== "string" || text.length === 0) return false;
  if (FORBIDDEN_CONTACT_CHANNEL_PATTERNS.some((re) => re.test(text))) {
    return true;
  }
  return containsBarePhoneNumber(text);
}

/**
 * De ENE plek waar een backend-tekst ("message") die als klantcopy in de
 * zakelijke flow terechtkomt, wordt gecontroleerd voordat hij de browser
 * bereikt. Bevat de tekst een verboden contactkanaal-belofte (of is hij
 * geen niet-lege string), dan komt de canonieke, gecontroleerde zin terug
 * i.p.v. de rauwe backend-tekst -- de HELE tekst wordt vervangen, niet
 * partieel gestript, zodat er nooit een halve/rare zin overblijft. Bron van
 * de fix: PR #1308 ronde 9 (blokkerend) -- zie het commentaar bij
 * `FORBIDDEN_CONTACT_CHANNEL_PATTERNS` hierboven.
 */
export function sanitizeZakelijkCustomerMessage(
  message,
  fallback = DEFAULT_SAFE_CUSTOMER_MESSAGE,
) {
  if (typeof message !== "string" || message.length === 0) return fallback;
  return containsForbiddenContactChannel(message) ? fallback : message;
}

// Codex-review PR #1308 ronde 13 (blokkerend, finding 1): `message`/
// `error`/`detail` uit een upstream NestJS/class-validator response is niet
// altijd een enkele string -- class-validator geeft vaak `message: [...]`
// (een array van veldfouten, soms met geneste objecten/arrays bij
// gegroepeerde constraints). `sanitizeZakelijkCustomerMessage` hierboven
// checkt alleen strings; een array werd door de routes tot deze ronde
// element-voor-element doorgegeven zonder ooit `containsForbiddenContact
// Channel` op de losse strings te draaien -- een backend-validatiefout die
// toevallig een telefoon/WhatsApp-zin bevat (of straks gaat bevatten) kon
// zo ongefilterd de browser bereiken.
function containsForbiddenContentDeep(value) {
  if (typeof value === "string") {
    return containsForbiddenContactChannel(value);
  }
  if (Array.isArray(value)) {
    return value.some(containsForbiddenContentDeep);
  }
  if (value !== null && typeof value === "object") {
    return Object.values(value).some(containsForbiddenContentDeep);
  }
  return false;
}

/**
 * Zelfde vervang-de-HELE-waarde-doctrine als `sanitizeZakelijkCustomerMessage`,
 * maar voor een veld dat ook een array (of geneste array/object daarvan) kan
 * zijn i.p.v. alleen een string. Bevat `value` ERGENS een string die een
 * verboden contactkanaal-belofte raakt, dan komt `fallback` (een enkele,
 * canonieke string) terug voor het HELE veld -- nooit een halfgesaneerde
 * lijst waarin alleen het ene rotte element vervangen is. Bevat `value`
 * nergens zoiets, dan komt hij ONGEWIJZIGD terug (vorm en al), zodat een
 * legitieme class-validator-foutenlijst intact blijft. `value === null`/
 * `undefined`/een primitief dat geen string is (getal, boolean) gaat
 * ongemoeid door `containsForbiddenContentDeep` heen (retourneert `false`
 * voor alles wat geen string/array/plain-object is) en komt dus ook
 * ongewijzigd terug.
 */
export function sanitizeZakelijkResponseField(
  value,
  fallback = DEFAULT_SAFE_CUSTOMER_MESSAGE,
) {
  if (typeof value === "string") {
    return sanitizeZakelijkCustomerMessage(value, fallback);
  }
  if (value === null || value === undefined) return value;
  return containsForbiddenContentDeep(value) ? fallback : value;
}
